"use client";

import mixpanel from "mixpanel-browser";
import { Auth0Provider } from "@auth0/auth0-react";
import Script from "next/script";
import { PostHogProvider } from "posthog-js/react";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import CookieConsent, { COOKIE_CONSENT_EVENT, COOKIE_CONSENT_KEY, getConsentMode } from "./CookieConsent";
import PostHogPageView from "./PostHogPageView";
import { captureEvent } from "@/src/helpers/trackEvent";
import { authRedirectUri } from "../helpers/authRedirect";

export default function AuthenticationAndAnalytics({ children }) {
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [posthogReady, setPosthogReady] = useState(false);
  const analyticsStarted = useRef(false);
  // Set only by a live consent decision (see `if (event)` below), never by
  // the initial mount sync — so the posthogReady effect only fires for an
  // actual button click, not every page load from a previously-consenting user.
  const pendingConsentChoice = useRef(null);

  useEffect(() => {
    const syncConsent = (event) => {
      // Native "storage" events fire for every localStorage write on the
      // origin (e.g. the editor's cursor-position/autosave keys from another
      // tab), not just consent changes — ignore anything that isn't the
      // consent key, or the loop re-captures consent_choice_made on unrelated
      // writes in other tabs for as long as both stay open.
      if (event && "key" in event && event.key !== COOKIE_CONSENT_KEY) {
        return;
      }
      let nextChoice = event?.detail;
      if (!nextChoice) {
        try {
          nextChoice = window.localStorage.getItem(COOKIE_CONSENT_KEY);
        } catch {
          nextChoice = null;
        }
      }
      if (nextChoice === "analytics" || nextChoice === "essential") {
        if (event) {
          pendingConsentChoice.current = nextChoice;
          // Captures immediately if posthog is already running (a mid-session
          // change); a first-ever accept hasn't initialized posthog yet at
          // this exact point, so this call safely no-ops and the posthogReady
          // effect below catches it once init completes.
          captureEvent("consent_choice_made", { choice: nextChoice });
        }
        setAnalyticsConsent(nextChoice === "analytics");
        return;
      }
      // No explicit choice: opt-in regions stay off, opt-out regions default
      // on, unless the browser sends Global Privacy Control (binding in CA).
      const gpc = typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
      setAnalyticsConsent(getConsentMode() === "optout" && !gpc);
    };
    syncConsent();
    window.addEventListener(COOKIE_CONSENT_EVENT, syncConsent);
    window.addEventListener("storage", syncConsent);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, syncConsent);
      window.removeEventListener("storage", syncConsent);
    };
  }, []);

  useEffect(() => {
    // Analytics only from the real app (same gate as the tracking scripts
    // below). Dev shares the PostHog/Mixpanel projects, so local testing was
    // landing in the prod dashboards (2026-09-03: 23 of 25 "book_search"
    // events came from the dev box). Never initialized means every
    // trackEvent/captureEvent call no-ops on its __loaded guard.
    if (process.env.NEXT_PUBLIC_ENV !== "production") {
      return;
    }
    if (analyticsConsent && !analyticsStarted.current) {
      mixpanel.init("a155dd5043501198778a9b64e6e5b21a", {
        track_pageview: true,
        persistence: "localStorage",
      });
      posthog.debug(false);
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        person_profiles: "identified_only",
        // Pageviews are captured manually via PostHogPageView on App Router
        // route changes instead, since the default capture fires before the
        // client-side route has resolved.
        capture_pageview: false,
        capture_pageleave: true,
        // Client-side crashes are otherwise invisible: they never reach the
        // backend, and $exception capture has been dark since 2026-08-02.
        capture_exceptions: true,
      });
      analyticsStarted.current = true;
      // Flip this on the NEXT render so PostHogPageView only mounts once
      // posthog.init() has already run in a prior commit — mounting it in
      // the same commit races React's child-before-parent effect order and
      // silently drops the first $pageview (posthog.capture() no-ops before
      // __loaded is set).
      setPosthogReady(true);
      return;
    }

    if (analyticsConsent && analyticsStarted.current) {
      mixpanel.opt_in_tracking();
      posthog.opt_in_capturing();
      return;
    }

    if (!analyticsConsent && analyticsStarted.current) {
      mixpanel.opt_out_tracking();
      posthog.opt_out_capturing();
    }
  }, [analyticsConsent]);

  // Catches the one case the synchronous capture above can't: a first-ever
  // "accept analytics" click, where posthog.init() hasn't run yet at click
  // time. posthogReady only transitions once per page load, so this can't
  // double-fire alongside the synchronous attempt.
  useEffect(() => {
    if (posthogReady && pendingConsentChoice.current) {
      captureEvent("consent_choice_made", { choice: pendingConsentChoice.current });
      pendingConsentChoice.current = null;
    }
  }, [posthogReady]);

  return (
    <>
      {process.env.NEXT_PUBLIC_ENV === "production" && analyticsConsent && (
        <>
          <Script
            id="upfluence-tracking"
            src="https://tracking.upfluence.co/js/v1/init-49da81ce-ce69-4e8d-b445-7c1016a57ab2.js"
            strategy="afterInteractive"
          />
          <Script
            type="text/javascript"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "j5fwef15ep");
          `,
            }}
          />
          <Script
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-WX4GJ2ZT');`,
            }}
          />
          <Script src="https://www.googletagmanager.com/gtag/js?id=AW-16646837895" strategy="afterInteractive" />
          <Script id="google-analytics" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){ dataLayer.push(arguments); }
              gtag('js', new Date());
              gtag('config', 'AW-16646837895');
            `}
          </Script>
        </>
      )}
      <Auth0Provider
        domain={`${process.env.NEXT_PUBLIC_AUTH0_DOMAIN}`}
        clientId={`${process.env.NEXT_PUBLIC_CLIENT_ID}`}
        authorizationParams={{
          redirect_uri: authRedirectUri(),
          audience: `${process.env.NEXT_PUBLIC_AUTH0_AUDIENCE}`,
        }}
        cacheLocation="localstorage"
        useRefreshTokens={true}
      >
        <PostHogProvider client={posthog}>
          {posthogReady && <PostHogPageView />}
          {children}
        </PostHogProvider>
        <CookieConsent />
      </Auth0Provider>
    </>
  );
}
